import { useState } from 'react';
import { Settings } from '../types';

interface Props {
  settings: Settings;
  onToggleTheme: () => void;
  onToggleMute: () => void;
  onClearLocation: () => void;
  onReset: () => void;
  onBack: () => void;
  hasSavedLocation: boolean;
}

export function SettingsPanel({
  settings,
  onToggleTheme,
  onToggleMute,
  onClearLocation,
  onReset,
  onBack,
  hasSavedLocation,
}: Props) {
  const [showResetWarning, setShowResetWarning] = useState(false);

  const handleResetConfirm = () => {
    setShowResetWarning(false);
    onReset();
  };

  return (
    <section>
      <div className="page-header">
        <div className="title-stack">
          <p className="eyebrow">Preferences</p>
          <h1>Settings</h1>
          <p className="helper-text">Your choices stay on this device.</p>
        </div>
        <button className="btn ghost" onClick={onBack}>
          Back
        </button>
      </div>
      <div className="settings-grid">
        <div className="toggle-row">
          <div>
            <h3>Theme</h3>
            <p className="helper-text">Switch between dark and light neon palettes.</p>
          </div>
          <button className="btn primary" onClick={onToggleTheme}>
            {settings.theme === 'dark' ? 'Use light' : 'Use dark'}
          </button>
        </div>
        <div className="toggle-row">
          <div>
            <h3>Mute</h3>
            <p className="helper-text">Reserved for future sound effects.</p>
          </div>
          <button className="btn ghost" onClick={onToggleMute}>
            {settings.muted ? 'Unmute' : 'Mute'}
          </button>
        </div>
        <div className="toggle-row">
          <div>
            <h3>Reset progress</h3>
            <p className="helper-text">Clear stats, daily history, achievements, and XP.</p>
          </div>
          <button className="btn ghost" onClick={() => setShowResetWarning(true)}>
            Reset
          </button>
        </div>
        <div className="toggle-row">
          <div>
            <h3>Location</h3>
            <p className="helper-text">Remove your saved location so local runs ask again next time.</p>
          </div>
          <button className="btn ghost" onClick={onClearLocation} disabled={!hasSavedLocation}>
            Remove location
          </button>
        </div>
      </div>
      {showResetWarning ? (
        <div className="dialog-backdrop" role="presentation">
          <div className="dialog-card" role="dialog" aria-modal="true" aria-labelledby="reset-title">
            <p className="eyebrow">Warning</p>
            <h2 id="reset-title">Reset all progress?</h2>
            <p className="helper-text">
              This will erase your stats, daily history, achievements, and XP on this device. This action cannot be
              undone.
            </p>
            <div className="dialog-actions">
              <button className="btn ghost" onClick={() => setShowResetWarning(false)}>
                Cancel
              </button>
              <button className="btn primary solid" onClick={handleResetConfirm}>
                Reset progress
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
